/**
 * websearch — SearXNG instance management + quick query.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/Card';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui/Tabs';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { StatusDot } from '../../components/ui/StatusDot';
import { EmptyState } from '../../components/ui/EmptyState';
import { Select } from '../../components/ui/Select';
import { Globe, Plus, RefreshCw, Search, Trash2, Upload, UploadCloud } from 'lucide-react';
import { themedConfirm } from '../../lib/themed-dialogs';

interface WebsearchInstance {
  name: string;
  db_status: 'provisioning' | 'running' | 'stopped' | 'error' | 'deleting';
  docker_status: 'running' | 'stopped' | 'missing';
  image: string;
  ports: { http: number };
  url: string;
  domain: string | null;
  created_at: string;
}

interface QueryResult {
  title: string;
  url: string;
  snippet: string;
  engine?: string;
  category?: string;
  published?: string | null;
}

function statusToBadge(s: WebsearchInstance['db_status']): 'running' | 'stopped' | 'error' | 'pending' {
  if (s === 'running') return 'running';
  if (s === 'stopped') return 'stopped';
  if (s === 'error') return 'error';
  return 'pending';
}

// ─── Instances Tab ────────────────────────────────────────────────────────

function InstancesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: 'default',
    language: 'cs',
    safe_search: 1,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['websearch-list'],
    queryFn: () => api.mcp<{ ok: boolean; count: number; instances: WebsearchInstance[] }>(
      'websearch.list', {},
    ),
    refetchInterval: 5000,
  });
  const instances = data?.instances ?? [];

  const deploy = useMutation({
    mutationFn: () => api.mcp('websearch.deploy', form as unknown as Record<string, unknown>),
    onSuccess: (r: { ok?: boolean; error?: string; message?: string }) => {
      if (r.ok === false) {
        toast.error(r.error ?? t('websearch.deployFailed'));
      } else {
        toast.success(r.message ?? t('websearch.deploying'));
        setAdding(false);
        qc.invalidateQueries({ queryKey: ['websearch-list'] });
      }
    },
  });

  const manage = useMutation({
    mutationFn: ({ name, action }: { name: string; action: 'start' | 'stop' | 'restart' }) =>
      api.mcp('websearch.manage', { name, action }),
    onSuccess: (r: { ok?: boolean; error?: string }, vars) => {
      if (r.ok === false) toast.error(r.error ?? t('websearch.actionFailed'));
      else toast.success(t('websearch.actionOk', { action: vars.action }));
      qc.invalidateQueries({ queryKey: ['websearch-list'] });
    },
  });

  const del = useMutation({
    mutationFn: (name: string) => api.mcp('websearch.delete', { name }),
    onSuccess: (r: { ok?: boolean; error?: string }) => {
      if (r.ok === false) toast.error(r.error ?? t('websearch.deleteFailed'));
      else {
        toast.success(t('websearch.deleted'));
        qc.invalidateQueries({ queryKey: ['websearch-list'] });
      }
    },
  });

  const publish = useMutation({
    mutationFn: (name: string) => api.mcp('websearch.publish', { name }),
    onSuccess: (r: { ok?: boolean; error?: string; url?: string }) => {
      if (r.ok === false) toast.error(r.error ?? t('websearch.publishFailed'));
      else toast.success(t('websearch.published', { url: r.url }));
      qc.invalidateQueries({ queryKey: ['websearch-list'] });
    },
  });

  const unpublish = useMutation({
    mutationFn: (name: string) => api.mcp('websearch.unpublish', { name }),
    onSuccess: (r: { ok?: boolean; error?: string }) => {
      if (r.ok === false) toast.error(r.error ?? t('websearch.unpublishFailed'));
      else toast.success(t('websearch.unpublished'));
      qc.invalidateQueries({ queryKey: ['websearch-list'] });
    },
  });

  return (
    <div className='space-y-4'>
      <div className='flex justify-between items-center'>
        <h2 className='text-lg font-semibold'>{t('websearch.instancesTitle')}</h2>
        <Button onClick={() => setAdding(true)} variant='primary'>
          <Plus className='w-4 h-4 mr-1' /> {t('websearch.deploy')}
        </Button>
      </div>

      {isLoading
        ? <div className='text-gray-400 text-sm'>{t('common.loading')}</div>
        : instances.length === 0
        ? (
          <EmptyState
            icon={Globe}
            title={t('websearch.emptyTitle')}
            description={t('websearch.emptyDesc')}
          />
        )
        : (
          <div className='space-y-2'>
            {instances.map((inst) => (
              <Card key={inst.name}>
                <CardContent className='py-3'>
                  <div className='flex items-center justify-between gap-4'>
                    <div className='flex-1'>
                      <div className='flex items-center gap-2 mb-1'>
                        <StatusDot status={statusToBadge(inst.db_status)} />
                        <span className='font-medium'>{inst.name}</span>
                        <Badge>{inst.db_status}</Badge>
                        {inst.docker_status !== inst.db_status &&
                          inst.db_status !== 'provisioning' && (
                          <Badge variant='warning'>
                            {t('websearch.driftBadge', { status: inst.docker_status })}
                          </Badge>
                        )}
                      </div>
                      <div className='text-xs text-gray-400 space-x-3'>
                        <span>{inst.image}</span>
                        <span>{t('websearch.portLabel', { port: inst.ports.http })}</span>
                        {inst.domain && (
                          <a
                            href={`https://${inst.domain}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-400 hover:underline'
                          >
                            https://{inst.domain}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className='flex gap-1'>
                      {inst.db_status === 'running'
                        ? (
                          <Button
                            variant='ghost'
                            onClick={() => manage.mutate({ name: inst.name, action: 'stop' })}
                          >
                            {t('websearch.stop')}
                          </Button>
                        )
                        : (
                          <Button
                            variant='ghost'
                            onClick={() => manage.mutate({ name: inst.name, action: 'start' })}
                          >
                            {t('websearch.start')}
                          </Button>
                        )}
                      <Button
                        variant='ghost'
                        onClick={() => manage.mutate({ name: inst.name, action: 'restart' })}
                      >
                        <RefreshCw className='w-4 h-4' />
                      </Button>
                      {inst.domain
                        ? (
                          <Button
                            variant='ghost'
                            onClick={() => unpublish.mutate(inst.name)}
                            title={t('websearch.unpublishTooltip')}
                          >
                            <Upload className='w-4 h-4' />
                          </Button>
                        )
                        : (
                          <Button
                            variant='ghost'
                            onClick={() => publish.mutate(inst.name)}
                            disabled={inst.db_status !== 'running'}
                            title={t('websearch.publishTooltip')}
                          >
                            <UploadCloud className='w-4 h-4' />
                          </Button>
                        )}
                      <Button
                        variant='danger'
                        onClick={async () => {
                          if (await themedConfirm(t('websearch.confirmDeleteInstance', { name: inst.name }))) {
                            del.mutate(inst.name);
                          }
                        }}
                      >
                        <Trash2 className='w-4 h-4' />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      {adding && (
        <Modal onClose={() => setAdding(false)} title={t('websearch.deployModalTitle')}>
          <div className='space-y-3 text-sm'>
            <label className='block'>
              <span className='text-gray-400 mb-1 block'>{t('websearch.nameSlugLabel')}</span>
              <input
                type='text'
                className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5'
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className='block'>
              <span className='text-gray-400 mb-1 block'>{t('websearch.defaultLanguageLabel')}</span>
              <input
                type='text'
                className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5'
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
              />
            </label>
            <label className='block'>
              <span className='text-gray-400 mb-1 block'>{t('websearch.safeSearchLabel')}</span>
              <Select
                className='w-full'
                value={form.safe_search}
                onChange={(e) => setForm({ ...form, safe_search: Number(e.target.value) })}
              >
                <option value={0}>{t('websearch.safeSearchOff')}</option>
                <option value={1}>{t('websearch.safeSearchModerate')}</option>
                <option value={2}>{t('websearch.safeSearchStrict')}</option>
              </Select>
            </label>
            <div className='flex justify-end gap-2'>
              <Button variant='ghost' onClick={() => setAdding(false)}>{t('common.cancel')}</Button>
              <Button
                variant='primary'
                onClick={() => deploy.mutate()}
                disabled={deploy.isPending}
              >
                {t('websearch.deploy')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Quick Search Tab ─────────────────────────────────────────────────────

function QueryTab() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['websearch-query', submitted],
    queryFn: () => api.mcp<{ ok: boolean; results: QueryResult[]; suggestions: string[] }>(
      'websearch.query',
      { q: submitted, n: 10 },
    ),
    enabled: !!submitted,
  });

  return (
    <div className='space-y-4'>
      <div className='flex gap-2'>
        <input
          type='text'
          placeholder={t('websearch.queryPlaceholder')}
          className='flex-1 bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm'
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setSubmitted(q)}
        />
        <Button variant='primary' onClick={() => setSubmitted(q)} disabled={!q}>
          <Search className='w-4 h-4 mr-1' /> {t('websearch.searchButton')}
        </Button>
      </div>

      {isLoading && <div className='text-gray-400 text-sm'>{t('websearch.searching')}</div>}

      {data?.suggestions && data.suggestions.length > 0 && (
        <div className='text-xs text-gray-400'>
          {t('websearch.suggestionsLabel')} {data.suggestions.slice(0, 6).join(' · ')}
        </div>
      )}

      {data?.results && (
        <div className='space-y-2'>
          {data.results.map((r, i) => (
            <Card key={i}>
              <CardContent className='py-3'>
                <a
                  href={r.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-400 hover:underline font-medium'
                >
                  {r.title}
                </a>
                <div className='text-xs text-gray-500 mb-1'>{r.url}</div>
                <div className='text-sm text-gray-300'>{r.snippet}</div>
                <div className='text-xs text-gray-500 mt-1 space-x-2'>
                  {r.engine && <span>{t('websearch.engineLabel', { engine: r.engine })}</span>}
                  {r.category && <span>{t('websearch.categoryLabel', { category: r.category })}</span>}
                  {r.published && <span>{r.published}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────

export default function WebsearchPage() {
  const { t } = useTranslation();
  return (
    <div className='p-4 space-y-4'>
      <Tabs defaultTab='instances'>
        <TabList>
          <Tab id='instances'>{t('websearch.tabInstances')}</Tab>
          <Tab id='query'>{t('websearch.tabQuickSearch')}</Tab>
        </TabList>
        <TabPanel id='instances'><InstancesTab /></TabPanel>
        <TabPanel id='query'><QueryTab /></TabPanel>
      </Tabs>
    </div>
  );
}
